/**
 * Tiny primitives reused across the three PortalAdmin tabs. Kept here
 * so neither tab needs to reach into the other to render a price, an
 * inline badge, or a centered modal.
 */
import { CheckCircle2 } from "lucide-react"; // (kept for future-proofing; tabs may import this if they want)
import { AccessibleModal } from "../../components/AccessibleModal";

export const CURRENCY_SYMBOLS = {
  GBP: "£", EUR: "€", USD: "$", AUD: "A$",
  CAD: "C$", NZD: "NZ$", CHF: "CHF ", JPY: "¥",
};

export function fmtPrice(value, currency) {
  const sym = CURRENCY_SYMBOLS[currency || "GBP"] || "£";
  if (value == null || value === "") return "—";
  const n = Number(value);
  if (!Number.isFinite(n)) return "—";
  if ((currency || "GBP") === "JPY") return `${sym}${n.toLocaleString(undefined, { maximumFractionDigits: 0 })}`;
  return `${sym}${n.toFixed(2)}`;
}

/** Generic centered modal with click-outside-to-close. */
export function Modal({ children, onClose }) {
  return (
    <AccessibleModal onClose={onClose}>{children}</AccessibleModal>
  );
}

/** Small pill — colour-coded background + border with a one-line label. */
export function Badge({ bg, fg, border, label }) {
  return (
    <span style={{
      display: "inline-block", padding: "0.15rem 0.55rem", borderRadius: "999px",
      background: bg, color: fg, fontSize: "0.7rem", fontWeight: 700,
      letterSpacing: "0.02em", border: `1px solid ${border}`, whiteSpace: "nowrap",
    }}>{label}</span>
  );
}

/** Submission lifecycle pill (pending/approved/auto_approved/rejected).
 *  Pass reviewStatus to show a secondary badge when the submission is held
 *  in the SKU reconciliation queue ("in_review") or cleared ("ready"). */
export function StatusPill({ status, reviewStatus }) {
  const colors = {
    pending: { bg: "rgba(255,216,79,0.15)", c: "#FFD84F" },
    approved: { bg: "rgba(24,240,184,0.15)", c: "#18F0B8" },
    auto_approved: { bg: "rgba(255,216,79,0.15)", c: "#FFD84F" },
    rejected: { bg: "rgba(240,80,248,0.15)", c: "#F050F8" },
  }[status] || { bg: "rgba(255,255,255,0.05)", c: "#888" };
  const label = status === "auto_approved" ? "Auto-approved ⚡" : status;
  return (
    <span style={{ display: "inline-flex", alignItems: "center", gap: "0.35rem", flexWrap: "wrap" }}>
      <span style={{ padding: "0.2rem 0.6rem", borderRadius: "10px", background: colors.bg, color: colors.c, fontSize: "0.75rem", fontWeight: "600", textTransform: "capitalize", border: `1px solid ${colors.c}` }}>{label}</span>
      {reviewStatus === "in_review" && (
        <span title="Held in SKU review queue pending reconciliation" style={{ padding: "0.15rem 0.5rem", borderRadius: "10px", background: "rgba(251,146,60,0.15)", color: "#fb923c", fontSize: "0.7rem", fontWeight: "600", border: "1px solid #fb923c" }}>SKU review</span>
      )}
    </span>
  );
}

// Re-export so consumers don't need to also pull from lucide just to get
// the same checkmark icon used in confirm tiles etc.
export { CheckCircle2 };
